import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ExternalLink, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { IMAGES } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { useSeo } from "@/hooks/useSeo";

function Stars({ n = 5 }) {
  return <div className="flex text-[var(--oxblood)]">{[...Array(Math.round(n))].map((_, i) => <Star key={i} className="w-4 h-4" fill="currentColor" />)}</div>;
}

export default function Reviews() {
  useSeo("Reviews | StoneCrest Exterior Specialists", "StoneCrest is a newly launched Lancashire exterior cleaning company. Genuine, verified customer reviews will be published here as our work builds up.", "https://stonecrestexteriors.co.uk/reviews");
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const hasContent = reviews.length > 0;
  const gRating = settings?.average_rating || 0;
  const gCount = settings?.review_count || 0;
  const reviewLink = settings?.google_review_link;

  return (
    <div className="-mt-20">
      {/* Light editorial hero band */}
      <section className="relative min-h-[46vh] flex items-end overflow-hidden" data-testid="reviews-hero">
        <img src={IMAGES.reviews} alt="Lancashire terraced street" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/55 to-white/5" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/70 via-transparent to-transparent" />
        <div className="relative px-6 md:px-12 max-w-7xl mx-auto w-full pb-12 pt-28">
          <p className="text-[11px] uppercase tracking-[0.35em] text-[var(--oxblood)] mb-4">Reviews</p>
          <h1 className="font-serif font-light text-5xl md:text-7xl leading-[0.98] tracking-tight text-[var(--ink)] max-w-3xl">Words from our customers.</h1>
          <p className="mt-5 text-[var(--muted)] max-w-xl">StoneCrest is a newly launched Lancashire business. Genuine, verified customer feedback will appear here as completed work builds up.</p>
        </div>
      </section>

      {hasContent ? (
        <>
          {gCount > 0 && (
            <section className="px-6 md:px-12 max-w-7xl mx-auto py-12">
              <div className="bg-[var(--ink)] text-white p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-8" data-testid="rating-banner">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-7xl md:text-8xl leading-none">{Number(gRating).toFixed(1)}</span>
                  <div><Stars n={gRating} /><p className="text-white/60 text-sm mt-1">Based on {gCount} reviews</p></div>
                </div>
                {reviewLink && <a href={reviewLink} target="_blank" rel="noopener noreferrer" data-testid="leave-review-btn" className="bg-white text-[var(--ink)] px-8 py-4 rounded-full tracking-wide hover:bg-white/90 transition-colors flex items-center gap-2">Leave a review <ExternalLink className="w-4 h-4" /></a>}
              </div>
            </section>
          )}

          <section className="px-6 md:px-12 max-w-7xl mx-auto py-8 pb-24">
            {reviews.length > 0 && (
              <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
                {reviews.map((r, i) => (
                  <Reveal key={r.id} delay={(i % 3) * 0.06} className="break-inside-avoid border border-[var(--border)] p-8 bg-white" data-testid={`review-${r.id}`}>
                    <Stars n={r.rating} />
                    <p className="font-serif text-xl leading-relaxed mt-3 mb-6">"{r.text}"</p>
                    <p className="text-sm text-[var(--muted)]">{r.author}{r.location ? ` · ${r.location}` : ""}{r.date ? ` · ${r.date}` : ""}</p>
                  </Reveal>
                ))}
              </div>
            )}
          </section>
          <CTASection />
        </>
      ) : (
        <section className="px-6 md:px-12 max-w-3xl mx-auto py-20 md:py-28">
          <Reveal>
            <div className="border border-[var(--border)] p-10 md:p-16 text-center bg-white" data-testid="reviews-empty-state">
              <h2 className="font-serif font-light text-3xl md:text-4xl leading-snug">Building our reputation, one property at a time.</h2>
              <p className="mt-6 text-[var(--muted)] leading-relaxed max-w-xl mx-auto">StoneCrest is a newly launched Lancashire business. Genuine customer feedback will be published here as completed work and verified reviews become available.</p>
              <Link to="/quote" data-testid="first-customer-btn" className="group mt-9 inline-flex items-center gap-2 bg-[var(--oxblood)] text-white px-9 py-4 rounded-full tracking-wide hover:bg-[var(--oxblood-hover)] transition-colors">
                Be Among Our First Customers <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
        </section>
      )}
    </div>
  );
}
